import {logger} from '@zoefin/logger';

import {
  KV_NAMESPACE,
  TOKEN_EXPIRY_LEEWAY_SECONDS,
  USER_TOKEN_TIMEOUT_MS,
} from '../../constants/request';
import {isBrowserRuntime} from '../../runtime/browser';
import {createRequestTimeoutSignal} from '../../runtime/signal';
import {
  ClientCredentialsProviderConfig,
  ClientCredentialsTarget,
  ClientToken,
} from '../../types/auth';
import {
  type ClientCredentialsResolution,
  RequestAuthTokenSource,
} from '../../types/route';

import {isClientCredentialsRequestAllowed} from './guard';
import {ClientTokenCacheGlobal} from './types';

const getClientTokenCache = (): Map<ClientCredentialsTarget, ClientToken> => {
  const globalScope = globalThis as ClientTokenCacheGlobal;

  if (!globalScope.__zoeRequestClientTokenCache) {
    globalScope.__zoeRequestClientTokenCache = new Map();
  }

  return globalScope.__zoeRequestClientTokenCache;
};

/** Checks the token is present and not expired (with a 60 s leeway to avoid edge-of-expiry failures). */
const isTokenUsable = (token?: ClientToken): token is ClientToken => {
  if (!token?.access_token) {
    return false;
  }

  if (!token.expires_at) {
    return true;
  }

  const now = Math.floor(Date.now() / 1000);

  return now < token.expires_at - TOKEN_EXPIRY_LEEWAY_SECONDS;
};

const parseStoredToken = (value: unknown): ClientToken | undefined => {
  if (!value) {
    return undefined;
  }

  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as ClientToken;
    } catch {
      return {access_token: value};
    }
  }

  if (typeof value === 'object') {
    return value as ClientToken;
  }

  return undefined;
};

const resolveKvConfig = (): {url: string; token: string} | undefined => {
  if (isBrowserRuntime()) {
    return undefined;
  }

  const url = process.env.KV_REST_API_URL;
  const token = process.env.KV_REST_API_TOKEN;

  if (!url || !token) {
    return undefined;
  }

  return {url, token};
};

/** Executes a single Vercel KV (Upstash) REST command. Returns `undefined` on any failure so callers can fall through. */
const executeKvCommand = async ({
  command,
  args,
}: {
  command: string;
  args: string[];
}): Promise<unknown> => {
  const config = resolveKvConfig();

  if (!config) {
    return undefined;
  }

  const path = [command, ...args.map(encodeURIComponent)].join('/');
  const endpoint = `${config.url}/${path}`;
  const {signal} = createRequestTimeoutSignal({
    timeoutMs: USER_TOKEN_TIMEOUT_MS,
  });

  try {
    const response = await fetch(endpoint, {
      headers: {
        Authorization: `Bearer ${config.token}`,
      },
      signal,
    });

    if (!response.ok) {
      return undefined;
    }

    const body = (await response.json()) as {result?: unknown};

    return body.result;
  } catch (error) {
    logger.warn(
      {
        error,
        endpoint,
      },
      'Failed to execute KV request for client credentials token',
    );

    return undefined;
  }
};

const readClientTokenFromKv = async (
  target: ClientCredentialsTarget,
): Promise<ClientToken | undefined> => {
  const raw = await executeKvCommand({
    command: 'hget',
    args: [KV_NAMESPACE, target],
  });

  return parseStoredToken(raw);
};

const writeClientTokenToKv = async (
  target: ClientCredentialsTarget,
  token: ClientToken,
): Promise<void> => {
  await executeKvCommand({
    command: 'hset',
    args: [KV_NAMESPACE, target, JSON.stringify(token)],
  });
};

const getClientCredentialsProviderConfig = (
  target: ClientCredentialsTarget,
): ClientCredentialsProviderConfig | undefined => {
  if (target === ClientCredentialsTarget.CORE_CC) {
    return {
      url: process.env.CORE_CLIENT_AUTH
        ? `${process.env.CORE_CLIENT_AUTH}/oauth/token`
        : undefined,
      body: {
        grant_type: process.env.CORE_CLIENT_GRANT_TYPE,
        client_id: process.env.CORE_CLIENT_ACCESS_ID,
        client_secret: process.env.CORE_CLIENT_ACCESS_SECRET,
        audience: process.env.CORE_CLIENT_AUDIENCE,
      },
    };
  }

  if (target === ClientCredentialsTarget.APOLLO_CC) {
    return {
      url: process.env.APOLLO_CLIENT_AUTH
        ? `${process.env.APOLLO_CLIENT_AUTH}/oauth/token`
        : undefined,
      body: {
        grant_type: process.env.APOLLO_CLIENT_GRANT_TYPE,
        client_id: process.env.APOLLO_CLIENT_ACCESS_ID,
        client_secret: process.env.APOLLO_CLIENT_ACCESS_SECRET,
        audience: process.env.APOLLO_CLIENT_AUDIENCE,
      },
    };
  }

  return {
    url: process.env.PLUTUS_CLIENT_AUTH
      ? `${process.env.PLUTUS_CLIENT_AUTH}/oauth/token`
      : undefined,
    body: {
      grant_type: process.env.PLUTUS_CLIENT_GRANT_TYPE,
      client_id: process.env.PLUTUS_CLIENT_ACCESS_ID,
      client_secret: process.env.PLUTUS_CLIENT_ACCESS_SECRET,
      audience: process.env.PLUTUS_CLIENT_AUDIENCE,
    },
  };
};

const fetchClientTokenFromProvider = async (
  target: ClientCredentialsTarget,
): Promise<ClientToken | undefined> => {
  const providerConfig = getClientCredentialsProviderConfig(target);

  if (!providerConfig?.url) {
    logger.error(
      {
        target,
      },
      'Client credentials provider URL is missing',
    );

    return undefined;
  }

  const hasMissingCredential = Object.values(providerConfig.body).some(
    (value) => !value,
  );

  if (hasMissingCredential) {
    logger.error(
      {
        target,
        hasGrantType: !!providerConfig.body.grant_type,
        hasClientId: !!providerConfig.body.client_id,
        hasClientSecret: !!providerConfig.body.client_secret,
        hasAudience: !!providerConfig.body.audience,
      },
      'Client credentials provider configuration is incomplete',
    );

    return undefined;
  }

  const {signal} = createRequestTimeoutSignal({
    timeoutMs: USER_TOKEN_TIMEOUT_MS,
  });

  try {
    const response = await fetch(providerConfig.url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(providerConfig.body),
      signal,
    });

    if (!response.ok) {
      logger.error(
        {
          target,
          status: response.status,
          statusText: response.statusText,
        },
        'Client credentials provider returned a non-OK response',
      );

      return undefined;
    }

    const token = (await response.json()) as ClientToken;

    if (!token.access_token) {
      logger.error(
        {
          target,
          responseKeys: Object.keys(token),
        },
        'Client credentials provider response did not include an access token',
      );

      return undefined;
    }

    const expiresAt =
      token.expires_in === undefined
        ? undefined
        : Math.floor(Date.now() / 1000) + token.expires_in;

    return {
      ...token,
      ...(expiresAt ? {expires_at: expiresAt} : {}),
    };
  } catch (error) {
    logger.error(
      {
        error,
        target,
      },
      'Failed to fetch client credentials token from provider',
    );

    return undefined;
  }
};

/**
 * Resolves a machine-to-machine (client-credentials) token for server-side requests.
 *
 * Lookup order:
 * 1. In-memory cache (shared across requests in the same process).
 * 2. Vercel KV (shared across serverless invocations).
 * 3. Auth0 client-credentials grant (writes back to both caches).
 */
export const resolveClientCredentialsAuthorization = async (
  target: ClientCredentialsTarget,
): Promise<ClientCredentialsResolution> => {
  // Gate first — before the cache — so a browser RPC that fails the app's
  // guard (e.g. a bot) can't read a token another request warmed.
  if (!(await isClientCredentialsRequestAllowed())) {
    return {target, tokenSource: RequestAuthTokenSource.NONE};
  }

  const cache = getClientTokenCache();
  const cachedToken = cache.get(target);

  if (isTokenUsable(cachedToken)) {
    return {
      target,
      token: cachedToken.access_token,
      tokenSource: RequestAuthTokenSource.CC_MEMORY_CACHE,
    };
  }

  // Browser callers cannot read KV or server-only provider env vars. Their
  // client-credentials calls run through the registered Server Action instead.
  if (isBrowserRuntime()) {
    return {
      target,
      tokenSource: RequestAuthTokenSource.NONE,
    };
  }

  const kvToken = await readClientTokenFromKv(target);

  if (isTokenUsable(kvToken)) {
    cache.set(target, kvToken);

    return {
      target,
      token: kvToken.access_token,
      tokenSource: RequestAuthTokenSource.CC_KV_CACHE,
    };
  }

  const refreshedToken = await fetchClientTokenFromProvider(target);

  if (!isTokenUsable(refreshedToken)) {
    return {
      target,
      tokenSource: RequestAuthTokenSource.NONE,
    };
  }

  cache.set(target, refreshedToken);
  // Best-effort cache population for other serverless instances — don't block the response
  writeClientTokenToKv(target, refreshedToken).catch(() => undefined);

  return {
    target,
    token: refreshedToken.access_token,
    tokenSource: RequestAuthTokenSource.CC_PROVIDER,
  };
};
